// client/src/pages/secure/CompliancePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const statusStyles = {
    'Pending Review': 'bg-yellow-100 text-yellow-800',
    'Approved': 'bg-green-100 text-green-800',
    'Rejected': 'bg-red-100 text-red-800',
    'Deferred': 'bg-blue-100 text-blue-800',
};

// --- Upload Document Modal ---
const UploadDocModal = ({ isOpen, onClose, onDocumentUploaded }) => {
    const [transactions, setTransactions] = useState([]);
    const [docFile, setDocFile] = useState(null);
    const [formData, setFormData] = useState({ document_name: '', transaction_id: '' });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();
    const token = localStorage.getItem('jwtToken');

    useEffect(() => {
        if (isOpen) {
            apiClient.get('/api/transactions')
                .then(data => {
                    setTransactions(data);
                    if (data.length > 0) {
                        setFormData(prev => ({ ...prev, transaction_id: data[0].transaction_id }));
                    }
                })
                .catch(() => setError('Could not load transactions.'));
            setFormData({ document_name: '', transaction_id: '' });
            setDocFile(null);
            setError('');
        }
    }, [isOpen, apiClient]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleFileChange = (e) => setDocFile(e.target.files[0]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!docFile) return setError('Please select a file to upload.');
        setError('');
        setIsSubmitting(true);

        const submissionData = new FormData();
        submissionData.append('document', docFile);
        submissionData.append('document_name', formData.document_name);
        submissionData.append('transaction_id', formData.transaction_id);
        submissionData.append('category', 'Transaction Document');

        try {
            const response = await fetch('/api/compliance/records', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: submissionData,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Upload failed');
            onDocumentUploaded(result);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Upload Compliance Document</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="document_name" value={formData.document_name} onChange={handleChange} placeholder="Document Name (e.g., 'Signed Listing Agreement')" required className="p-2 border rounded w-full" />
                    <select name="transaction_id" value={formData.transaction_id} onChange={handleChange} required className="w-full p-2 border rounded">
                        <option value="">Select Transaction</option>
                        {transactions.map(t => <option key={t.transaction_id} value={t.transaction_id}>{t.street_address}</option>)}
                    </select>
                    <input type="file" onChange={handleFileChange} required className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">{isSubmitting ? 'Uploading...' : 'Upload'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const CompliancePage = () => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();

    const fetchDocuments = useCallback(() => {
        setIsLoading(true);
        setError(null);
        apiClient.get('/api/compliance/records')
            .then(data => setDocuments(data))
            .catch(err => setError(err.message || 'Failed to fetch documents.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    return (
        <>
            <UploadDocModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onDocumentUploaded={fetchDocuments}
            />
            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-slate-800">Transaction Document Review</h1>
                    <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                        Upload Document
                    </button>
                </div>
                
                {isLoading && <p>Loading documents...</p>}
                {error && <p className="text-red-500">Error: {error}</p>}
                {!isLoading && !error && (
                    <div className="overflow-x-auto">
                        <div className="p-4"><DataList rows={documents} rowKey="record_id" csvName="compliance-documents" defaultSort={{ key: 'upload_timestamp', dir: 'desc' }} empty="No compliance documents found."
                            filters={[{ key: 'review_status', label: 'Status' }, { key: 'agent', label: 'Uploaded by' }]}
                            columns={[
                                { key: 'document_name', label: 'Document Name', render: (doc) => <span className="font-medium text-slate-900">{doc.document_name}</span> },
                                { key: 'street_address', label: 'Transaction Property' },
                                { key: 'agent', label: 'Uploaded By', value: (doc) => `${doc.agent_first_name || ''} ${doc.agent_last_name || ''}`.trim() },
                                { key: 'upload_timestamp', label: 'Date Uploaded', type: 'date' },
                                { key: 'review_status', label: 'Status', render: (doc) => <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[doc.review_status] || 'bg-gray-100 text-gray-800'}`}>{doc.review_status}</span> },
                                { key: 'actions', label: 'Action', sortable: false, csv: false, search: false, render: (doc) => <Link to={`/dashboard/compliance/${doc.record_id}`} className="text-indigo-600 hover:text-indigo-900">Review</Link> },
                            ]} /></div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CompliancePage;
